import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';

const STORAGE_KEY = 'pav_locale';

const messages = {
  en: {
    'catalog.title': 'Catalog',
    'catalog.search': 'Search assets…',
    'catalog.printReady': '3D print ready',
    'catalog.manifold': 'Manifold mesh',
    'catalog.watertight': 'Watertight',
    'checkout.buy': 'Buy now',
    'checkout.addToCart': 'Add to cart',
    'checkout.licenseAck': 'I agree to the license terms for all items in this purchase.',
    'nav.briefs': 'Project briefs',
    'nav.challenges': 'Challenges',
    'nav.browse': 'Browse',
    'nav.creators': 'Creators',
    'legal.terms': 'Terms of Use',
    'legal.privacy': 'Privacy Policy',
    'legal.cookies': 'Cookie Policy',
    'legal.refund': 'Refund Policy',
    'legal.copyright': 'Copyright Policy',
    'footer.tagline': 'A marketplace for 3D assets',
  },
  es: {
    'catalog.title': 'Catálogo',
    'catalog.search': 'Buscar recursos…',
    'catalog.printReady': 'Listo para impresión 3D',
    'catalog.manifold': 'Malla manifold',
    'catalog.watertight': 'Hermético',
    'checkout.buy': 'Comprar ahora',
    'checkout.addToCart': 'Añadir al carrito',
    'checkout.licenseAck': 'Acepto los términos de licencia de todos los artículos de esta compra.',
    'nav.briefs': 'Encargos personalizados',
    'nav.challenges': 'Retos',
    'nav.browse': 'Explorar',
    'nav.creators': 'Creadores',
    'legal.terms': 'Términos de uso',
    'legal.privacy': 'Política de privacidad',
    'legal.cookies': 'Política de cookies',
    'legal.refund': 'Política de reembolso',
    'legal.copyright': 'Política de derechos de autor',
    'footer.tagline': 'Un mercado de recursos 3D',
  },
  de: {
    'catalog.title': 'Katalog',
    'catalog.search': 'Assets suchen…',
    'catalog.printReady': '3D-Druck bereit',
    'catalog.manifold': 'Manifold-Mesh',
    'catalog.watertight': 'Wasserdicht',
    'checkout.buy': 'Jetzt kaufen',
    'checkout.addToCart': 'In den Warenkorb',
    'checkout.licenseAck': 'Ich akzeptiere die Lizenzbedingungen für alle Artikel in diesem Kauf.',
    'nav.briefs': 'Projektaufträge',
    'nav.challenges': 'Challenges',
    'nav.browse': 'Durchsuchen',
    'nav.creators': 'Creators',
    'legal.terms': 'Nutzungsbedingungen',
    'legal.privacy': 'Datenschutz',
    'legal.cookies': 'Cookie-Richtlinie',
    'legal.refund': 'Rückerstattungsrichtlinie',
    'legal.copyright': 'Urheberrecht',
    'footer.tagline': 'Ein Marktplatz für 3D-Assets',
  },
};

const I18nContext = createContext({ locale: 'en', t: (k) => k, setLocale: () => {} });

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || 'en';
    } catch {
      return 'en';
    }
  });

  const setLocale = (next) => {
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch (_) { /* ignore */ }
  };

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo(() => ({
    locale,
    setLocale,
    t: (key) => messages[locale]?.[key] || messages.en[key] || key,
  }), [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}

export const SUPPORTED_LOCALES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'de', label: 'Deutsch' },
];
